import React from "react";
import SEO from "../components/SEO";

const About = () => {
  return (
    <>
      <SEO
        title="About"
        description="Blank Beanies"
        keywords="Blank Beanies"
      />

      <div className="min-h-screen bg-[#f5f6f5]">
        <div className="container mx-auto max-w-7xl px-4 py-24">
          {/* Hero Section */}
          <div className="relative mb-24 overflow-hidden rounded-[28px] bg-[#1e242a] px-4 py-14 text-center sm:px-6 md:px-10 md:py-20 lg:px-14">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1e242a] via-[#37404a] to-[#5f6870]"></div>
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl"></div>
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#e5e8e6]/10 blur-3xl"></div>

            <div className="relative z-10 mx-auto max-w-5xl">
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-6 py-2 text-md font-bold text-[#e5e8e6] shadow-sm backdrop-blur-sm">
                Premium Custom Hat Patches
              </span>

              <h1 className="text-4xl font-extrabold leading-tight text-white md:text-6xl">
                About{" "}
                <span className="text-[#e5e8e6]">
                  Personalised Patches
                </span>
                <br />
                <span className="text-3xl text-white/70 md:text-4xl">
                  for Hats
                </span>
              </h1>

              <p className="mx-auto mt-6 max-w-3xl text-lg text-white/85 md:text-xl">
                Your trusted partner for premium, durable, and beautifully crafted
                custom patches designed specifically for hats & caps.
              </p>
            </div>
          </div>

          {/* Intro */}
          <section className="mb-24 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-12">
            <div className="rounded-[28px] border border-[#1e242a]/10 bg-white p-8 shadow-[0_20px_55px_rgba(30,36,42,0.10)] md:p-12 lg:col-span-7">
              <h2 className="mb-5 text-3xl font-bold text-[#1e242a]">
                The Patch Partner Customers Trust
              </h2>

              <p className="max-w-4xl text-lg leading-relaxed text-[#5f6870]">
                At <strong className="text-[#1e242a]">Personalised Patches for Hats</strong>, we believe creating
                custom hat patches should be effortless, fast, and consistently
                high-quality. Whether you’re building a brand, outfitting a team,
                or launching merchandise, we simplify the entire process — from
                design to delivery.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-[28px] bg-[#1e242a] p-8 text-white shadow-[0_20px_55px_rgba(30,36,42,0.16)] md:p-12 lg:col-span-5">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1e242a] via-[#37404a] to-[#5f6870]"></div>
              <div className="absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-[#e5e8e6]/10 blur-3xl"></div>

              <div className="relative z-10 flex h-full flex-col justify-center">
                <h2 className="text-3xl font-bold text-white">
                  The Patch Partner Customers Trust
                </h2>

                <p className="mt-5 text-lg leading-relaxed text-white/85">
                  At Personalised Patches for Hats, we believe creating custom hat patches should be effortless, fast, and consistently high-quality.
                </p>
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <section className="mb-24">
            <h2 className="mb-12 text-3xl font-bold text-[#1e242a]">
              What We Specialize In
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[
                "Embroidered, Woven, PVC, Chenille, Leather & Bullion Patches",
                "Custom Shapes, Premium Finishes & Full-Color Designs",
                "Patches Engineered Specifically for Hats & Caps",
                "Easy Online Ordering in Minutes",
                "Low Minimums & Bulk Discounts",
                "Fast Turnaround with Rush Options",
              ].map((item, index) => (
                <div
                  key={index}
                  className={`group rounded-[26px] border border-[#1e242a]/10 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                    index % 2 === 1 ? "lg:mt-8" : ""
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-[#1e242a] text-lg font-bold text-white transition-all duration-300 group-hover:bg-[#37404a]">
                      ✔
                    </span>

                    <p className="font-medium leading-relaxed text-[#1e242a]">
                      {item}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="mb-24 overflow-hidden rounded-[32px] bg-white shadow-[0_24px_70px_rgba(30,36,42,0.12)]">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="relative overflow-hidden bg-[#1e242a] p-8 text-white md:p-12 lg:col-span-5">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1e242a] via-[#37404a] to-[#5f6870]"></div>
                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl"></div>

                <div className="relative z-10">
                  <h2 className="text-3xl font-bold text-white">
                    What Sets Us Apart
                  </h2>

                  <p className="mt-8 max-w-3xl text-white/80">
                    We’re more than patch manufacturers — we help brands, creators,
                    and organizations bring their identity to life with confidence.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 p-8 md:grid-cols-2 md:p-12 lg:col-span-7">
                <p className="rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-5 text-lg text-[#1e242a]">
                  ⚡ Instant pricing — no waiting for quotes
                </p>

                <p className="rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-5 text-lg text-[#1e242a]">
                  🚀 Fast production & reliable delivery
                </p>

                <p className="rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-5 text-lg text-[#1e242a]">
                  🤝 Real human support — no bots
                </p>

                <p className="rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-5 text-lg text-[#1e242a]">
                  🎯 Precision craftsmanship & strict quality control
                </p>
              </div>
            </div>
          </section>

          {/* Trust & Authority */}
          <section className="mb-24">
            <h2 className="mb-12 text-3xl font-bold text-[#1e242a]">
              Authority, Experience & Trust
            </h2>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                {
                  title: "Decades of Experience",
                  desc: "Serving businesses, brands & organizations across the USA.",
                },
                {
                  title: "Premium Quality Control",
                  desc: "Durable stitching, strong adhesion & flawless detailing.",
                },
                {
                  title: "Satisfaction Guaranteed",
                  desc: "Transparent communication & customer-first service.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="rounded-[28px] border border-[#1e242a]/10 bg-white p-8 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e5e8e6] text-sm font-bold text-[#1e242a]">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="mb-3 text-xl font-bold text-[#1e242a]">
                    {item.title}
                  </h3>

                  <p className="text-[#5f6870]">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Media Division */}
          <section className="overflow-hidden rounded-[32px] border border-[#1e242a]/10 bg-white shadow-[0_24px_70px_rgba(30,36,42,0.10)]">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="bg-[#e5e8e6] p-8 md:p-12 lg:col-span-4">
                <h2 className="text-3xl font-bold text-[#1e242a]">
                  Our Media & Publication Division
                </h2>
              </div>

              <div className="p-8 md:p-12 lg:col-span-8">
                <p className="max-w-4xl text-lg leading-relaxed text-[#5f6870]">
                  To share our expertise beyond manufacturing, we launched
                  <strong className="text-[#1e242a]"> EmbroiderCraft Studio — Powered by Personalised Patches for Hats</strong>.
                  Through in-depth tutorials, guides, and articles, we help embroidery
                  professionals, hobbyists, and businesses master the art of patch creation.
                </p>

                <p className="mt-4 text-[#5f6870]">
                  Our mission is simple: make professional embroidery knowledge accessible to everyone.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default About;